import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { UserPlus } from 'lucide-react';

import { supplierRegister } from '@/services/auth';
import { useAuthStore } from '@/store/auth.store';
import { Field } from '@/components/fields';
import logoWide from '@/images/Resstologo2.png';

type Errors = Partial<Record<'name' | 'email' | 'password' | 'city', string>>;

export default function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  const validate = (): boolean => {
    const e: Errors = {};
    if (name.trim().length < 2) e.name = 'Укажите название компании';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Некорректный email';
    if (password.length < 6) e.password = 'Минимум 6 символов';
    if (city.trim().length < 2) e.city = 'Укажите город';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    try {
      setSubmitting(true);
      const res = await supplierRegister({
        email: email.trim(),
        password,
        name: name.trim(),
        phone: phone.trim() || undefined,
        city: city.trim(),
      });
      setSession({
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
        supplier: res.supplier,
      });
      toast.success('Аккаунт создан');
      navigate('/', { replace: true });
    } catch {
      // тост от интерсептора
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-full flex items-center justify-center p-4 py-10">
      <div className="w-full max-w-md card p-7">
        <div className="flex flex-col items-center text-center mb-6">
          <img src={logoWide} alt="Resstro" className="h-12 w-auto object-contain mb-3" />
          <div className="text-sm text-ink/50">Регистрация поставщика</div>
        </div>

        <form className="space-y-4" onSubmit={onSubmit}>
          <Field label="Название компании" required error={errors.name}>
            <input
              className="input"
              placeholder="ООО «Овощная база»"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </Field>

          <Field label="Email" required error={errors.email}>
            <input
              type="email"
              className="input"
              placeholder="you@company.ru"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputMode="email"
              autoComplete="email"
            />
          </Field>

          <Field label="Пароль" required error={errors.password} hint="Минимум 6 символов">
            <input
              type="password"
              className="input"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </Field>

          <Field label="Город" required error={errors.city}>
            <input
              className="input"
              placeholder="Москва"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </Field>

          <Field label="Телефон" hint="Необязательно">
            <input
              className="input"
              placeholder="+7 900 000-00-00"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
              autoComplete="tel"
            />
          </Field>

          <label className="flex items-start gap-2.5 text-sm text-ink/70 cursor-pointer">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-line text-orange focus:ring-orange/30"
            />
            <span>
              Я ознакомлен с{' '}
              <a href="https://resstro.ru/offer" target="_blank" rel="noreferrer" className="text-orange-600 hover:underline">
                офертой
              </a>{' '}
              и{' '}
              <a href="https://resstro.ru/privacy" target="_blank" rel="noreferrer" className="text-orange-600 hover:underline">
                политикой конфиденциальности
              </a>
            </span>
          </label>

          <button className="btn-primary w-full" type="submit" disabled={submitting || !agree}>
            <UserPlus size={16} /> {submitting ? 'Создаём…' : 'Зарегистрироваться'}
          </button>

          <p className="text-center text-xs text-ink/40">
            3 дня бесплатно, далее 2999 ₽/мес. Без привязки карты на пробный период.
          </p>
        </form>

        <div className="mt-5 text-sm text-ink/60 text-center">
          Уже есть аккаунт?{' '}
          <Link to="/login" className="text-orange-600 font-medium hover:underline">
            Войти
          </Link>
        </div>
      </div>
    </div>
  );
}
